import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import GroupIcon from '@mui/icons-material/Group';
import { Button } from '../../../components/ui/Button';
import { CouponCountBadge } from '../../coupons/components';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@lov/shared';

interface BookingWidgetProps {
  venueId: string;
  basePrice: number;
  currency?: string;
  maxCapacity?: number;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  onBookNow: () => void;
}

const WidgetContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 24,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  padding: 24,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
}));

const PriceSection = styled(Box)({
  marginBottom: 20,
});

const PriceRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 4,
});

const Price = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '2rem',
  fontWeight: 700,
  color: theme.palette.success.main,
}));

const PriceUnit = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '1rem',
  color: theme.palette.text.secondary,
}));

const CapacityRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8,
  color: theme.palette.text.secondary,
}));

const CapacityText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const BookButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: 12,
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  transition: 'all 0.2s ease',
  boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}`,
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.35)}`,
  },
}));

const ContactSection = styled(Box)({
  marginTop: 20,
});

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: 12,
}));

const ContactList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const ContactItem = styled('a')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 14px',
  backgroundColor: alpha(theme.palette.success.main, 0.05),
  borderRadius: 10,
  textDecoration: 'none',
  color: theme.palette.text.primary,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    transform: 'translateX(4px)',
  },
}));

const ContactIconWrapper = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.success.main,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
}));

const ContactText = styled(Box)({
  flex: 1,
});

const ContactLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));

const ContactValue = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const WhatsAppIconStyled = styled(WhatsAppIcon)({
  color: '#25D366',
});

export function BookingWidget({
  venueId,
  basePrice,
  currency = 'INR',
  maxCapacity,
  phone,
  whatsapp,
  email,
  onBookNow,
}: BookingWidgetProps) {
  const { isEnabled: isBookNowEnabled } = useFeatureFlag(FEATURE_FLAGS.BOOK_NOW);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatPhoneForLink = (phoneNumber: string) => {
    return phoneNumber.replace(/[^0-9+]/g, '');
  };

  const hasContactInfo = phone || whatsapp || email;

  return (
    <WidgetContainer>
      {/* Price Section */}
      <PriceSection>
        <PriceRow>
          <Price>{formatPrice(basePrice)}</Price>
          <PriceUnit>/ hour</PriceUnit>
        </PriceRow>
        {maxCapacity && (
          <CapacityRow>
            <GroupIcon sx={{ fontSize: 18 }} />
            <CapacityText>Up to {maxCapacity} guests</CapacityText>
          </CapacityRow>
        )}
      </PriceSection>

      {/* Coupon Badge */}
      <Box sx={{ mb: 2 }}>
        <CouponCountBadge venueId={venueId} />
      </Box>

      {/* Book Now Button */}
      <BookButton onClick={isBookNowEnabled ? onBookNow : undefined} disabled={!isBookNowEnabled}>
        {isBookNowEnabled ? 'Book Now' : 'Coming Soon'}
      </BookButton>

      {/* Contact Section */}
      {hasContactInfo && (
        <ContactSection>
          <Divider sx={{ my: 2 }} />
          <SectionLabel>Contact Venue</SectionLabel>
          <ContactList>
            {phone && (
              <ContactItem href={`tel:${formatPhoneForLink(phone)}`}>
                <ContactIconWrapper>
                  <PhoneIcon sx={{ fontSize: 20 }} />
                </ContactIconWrapper>
                <ContactText>
                  <ContactLabel>Call</ContactLabel>
                  <ContactValue>{phone}</ContactValue>
                </ContactText>
              </ContactItem>
            )}

            {whatsapp && (
              <ContactItem
                href={`https://wa.me/${formatPhoneForLink(whatsapp)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ContactIconWrapper>
                  <WhatsAppIconStyled sx={{ fontSize: 20 }} />
                </ContactIconWrapper>
                <ContactText>
                  <ContactLabel>WhatsApp</ContactLabel>
                  <ContactValue>{whatsapp}</ContactValue>
                </ContactText>
              </ContactItem>
            )}

            {email && (
              <ContactItem href={`mailto:${email}`}>
                <ContactIconWrapper>
                  <EmailIcon sx={{ fontSize: 20 }} />
                </ContactIconWrapper>
                <ContactText>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>{email}</ContactValue>
                </ContactText>
              </ContactItem>
            )}
          </ContactList>
        </ContactSection>
      )}
    </WidgetContainer>
  );
}
